import React, { useEffect, useState } from 'react'
import Spinner from 'components/Spinner'
import './styles/manageTeam.scss'
import { DotsThree, PencilSimple, Trash, UserCircle, Users } from '@phosphor-icons/react'
import { Avatar, Dropdown, MenuProps } from 'antd'
import { getNameSignature } from 'helper/utility'
import Chip from 'components/global/Chip'
import { useDeleteTeamInLeague, useGetLeagueTeam } from 'api/Team/LeagueTeam'
import { leagueEventStore } from 'state/league/leagueEvent'
import { LeagueTeamResp } from 'types/team/leagueTeam'
import { manageTeamStore } from 'state/league/manageTeam'
import AlertModal from 'helper/AlertModal'
import { useHistory } from 'react-router-dom'

const ManageTeam: React.FC = () => {
  const history = useHistory()
  const { leagueId } = leagueEventStore(store => ({
    leagueId: store.leagueData._id,
  }))
  const { selectedTeam, setSelectedTeam, setAddTeamModal, setManageTeamModal } =
    manageTeamStore(store => ({
      selectedTeam: store.selectedTeam,
      setSelectedTeam: store.setSelectedTeam,
      setAddTeamModal: store.setAddTeamModal,
      setManageTeamModal: store.setManageTeamModal,
    }))
  const [alertModal, setAlertModal] = useState<boolean>(false)
  const {
    isLoading,
    isFetching,
    refetch,
    isError,
    data: teams,
  } = useGetLeagueTeam(leagueId)
  const { mutate: deleteTeamMutation, isLoading: deleteTeamMutationLoading } =
    useDeleteTeamInLeague()

  const items = [
    {
      key: 'view-profile',
      label: (
        <div className="flex align-center gap-10px">
          <UserCircle size={20} color="#000" />
          <span>View Profile</span>
        </div>
      ),
    },
    {
      key: 'edit',
      label: (
        <div className="flex align-center gap-10px">
          <PencilSimple size={20} color="#000" />
          <span>Edit</span>
        </div>
      ),
    },
    {
      key: 'delete',
      label: (
        <div className="flex align-center gap-10px">
          <Trash size={20} color="#ff6961" />
          <span className="danger-txt">Delete</span>
        </div>
      ),
    },
  ]

  const handleMenuClick = (
    e: Parameters<MenuProps['onClick']>[0],
    item: LeagueTeamResp,
  ) => {
    setSelectedTeam(item)
    if (e.key === 'view-profile') {
      history.push(`/spaces/team-profile/${item.team._id}`)
    } else if (e.key === 'edit') {
      setAddTeamModal(true)
      setManageTeamModal(false)
    } else {
      setAlertModal(true)
    }
  }

  const handleYesDelete = () => {
    deleteTeamMutation(selectedTeam._id, {
      onSuccess: () => {
        setSelectedTeam(null)
        setAlertModal(false)
      },
    })
  }

  useEffect(() => {
    refetch()
  }, [])

  if (isError) {
    return (
      <div className="flex justify-center items-center py-40px">
        <p>Something went wrong while loading teams</p>
      </div>
    )
  }

  return (
    <>
      {isLoading || isFetching ? (
        <div className="py-40px flex justify-center items-center">
          <Spinner />
        </div>
      ) : teams.length > 0 ? (
        teams.map(item => {
          return (
            <div className="team-container" key={item._id}>
              <div className="general-container">
                <div className="team-content">
                  <div className="w-40px h-40px">
                    <Avatar size={40} src={item.team.profilePhoto?.thumb}>
                      {getNameSignature(item.team.name)}
                    </Avatar>
                  </div>
                  <div>
                    <h3 className="text-14 w-500">{item.team.name}</h3>
                    <div className="chip-container">
                      <Chip text={item.division.name} />
                      {item.division.genders?.map((gender, index) => (
                        <Chip key={`gender-${index}`} text={gender} />
                      ))}
                      {item.division.ageGroups?.map((ageGroup, index) => (
                        <Chip key={`age-${index}`} text={ageGroup.name} />
                      ))}
                      {item.division.skillLevels?.map((skillLevel, index) => (
                        <Chip key={`skill-${index}`} text={skillLevel.name} />
                      ))}
                      {item.division.regions?.map((region, index) => (
                        <Chip key={`region-${index}`} text={region} />
                      ))}
                    </div>
                  </div>
                </div>
                <Dropdown
                  trigger={['click']}
                  menu={{ items, onClick: e => handleMenuClick(e, item) }}
                  placement="bottomRight"
                  arrow={false}
                  overlayStyle={{ width: '160px' }}
                >
                  <DotsThree size={20} color="#000" />
                </Dropdown>
              </div>
              <div className="pricing-cont">
                <div className="flex align-center gap-10px">
                  <Users size={20} color="#000" />
                  <p className="text-14 text-black-500">
                    {`${item.minPlayers} - ${item.maxPlayers} Players`}
                  </p>
                </div>
                <p>${item.price.toFixed(2)}</p>
              </div>
            </div>
          )
        })
      ) : (
        <div className="mt-20px">
          <p className="text-gray">No teams created yet</p>
        </div>
      )}

      {alertModal && (
        <AlertModal
          isModalVisible={alertModal}
          setIsModalVisible={setAlertModal}
          data={{
            name: 'League Team',
            heading: 'Delete Team',
            message1: 'Are you sure you want to delete this team?',
            okText: 'Yes, delete',
          }}
          onYesClick={handleYesDelete}
          loading={deleteTeamMutationLoading}
        />
      )}
    </>
  )
}

export default ManageTeam
